// Self-Service Vetting Engine v1.0.3
import { v4 as uuidv4 } from 'uuid';
import { ESSVNode, ESSVOutcome, ESSVRunnerState } from './types/selfServeVetting';
export class SSVValidator {
    constructor(config) {
        this.config = config;
    }
    validateNode(node) {
        // Check if all required fields are filled
        if (!node.label || !node.nodeType) {
            return false;
        }
        // Check if the nodeType is valid
        if (![ESSVNode.ASSESSMENT, ESSVNode.REQUIREMENT].includes(node.nodeType)) {
            return false;
        }
        // If the node has a nextNode, check that it refers to a valid node ID
        // We can use the config's getNode method to check the existence of a node
        if (node.nextNode && !this.config.getNode(node.nextNode)) {
            return false;
        }
        // Additional specific validations if any can be added here
        return true;
    }
    validateOption(option) {
        // Check if all required fields are filled
        if (!option.label) {
            return false;
        }
        // If the option has a nextNode, check that it refers to a valid node ID
        if (option.nextNode && !this.config.getNode(option.nextNode)) {
            return false;
        }
        // Check if the outcome field has a valid value
        if (option.outcome && ![ESSVOutcome.APPROVED, ESSVOutcome.REJECTED, ESSVOutcome.PIPELINE].includes(option.outcome)) {
            return false;
        }
        // Additional specific validations if any can be added here
        return true;
    }
    validateRequirement(requirement) {
        // Check if all required fields are filled
        if (!requirement.label || !requirement.categoryId) {
            return false;
        }
        // If the requirement has a nextNode, check that it refers to a valid node ID
        if (requirement.nextNode && !this.config.getNode(requirement.nextNode)) {
            return false;
        }
        // Check if the outcome field has a valid value
        if (requirement.outcome && ![ESSVOutcome.APPROVED, ESSVOutcome.REJECTED, ESSVOutcome.PIPELINE].includes(requirement.outcome)) {
            return false;
        }
        // Additional specific validations if any can be added here
        return true;
    }
    validateSingleRequirementNode() {
        const requirementNodes = this.config.getRequirementNodes();
        return requirementNodes.length === 1;
    }
    detectOrphanNodes() {
        // An orphan node is a node that is not reachable from the start node
        // To find orphan nodes, we start from the start node and mark all reachable nodes
        // Then we check if there are any nodes that are not marked
        // We return true when an orphan node was found
        const visitedNodes = new Set();
        const startNode = this.config.getStartNode();
        const visitNode = (node) => {
            visitedNodes.add(node.id);
            if (node.nodeType === ESSVNode.REQUIREMENT && node.requirements) {
                for (const requirement of node.requirements) {
                    if (requirement.nextNode && !visitedNodes.has(requirement.nextNode)) {
                        const nextNode = this.config.getNode(requirement.nextNode);
                        if (!nextNode) {
                            // Something went wrong, we have already checked that the next nodes of all options are valid
                            throw new Error(`Node with ID ${requirement.nextNode} not found while traversing from requirement with ID ${requirement.id}`);
                        }
                        visitNode(nextNode);
                    }
                }
            }
            else {
                for (const option of node.options) {
                    if (option.nextNode && !visitedNodes.has(option.nextNode)) {
                        const nextNode = this.config.getNode(option.nextNode);
                        if (!nextNode) {
                            // Something went wrong, we have already checked that the next nodes of all options are valid
                            throw new Error(`Node with ID ${option.nextNode} not found while traversing from option with ID ${option.id}`);
                        }
                        visitNode(nextNode);
                    }
                }
            }
            if (node.nextNode) {
                const nextNode = this.config.getNode(node.nextNode);
                if (nextNode) {
                    visitNode(nextNode);
                }
            }
        };
        visitNode(startNode);
        // Check if there are nodes that were not visited
        for (const node of Array.from(this.config.nodeMap.values())) {
            if (!visitedNodes.has(node.id)) {
                return true;
            }
        }
        return false;
    }
    detectCircularReferences() {
        // A circular reference is a loop in the node graph where a node can reach itself through a series of connections
        // We can detect circular references using depth-first search with a stack to keep track of the nodes in the current path
        // If we encounter a node that is already in the current path, it indicates a circular reference
        // We return true when a circular reference was found
        const startNode = this.config.getStartNode();
        const dfs = (node, visitedNodes, stack) => {
            visitedNodes.add(node.id);
            stack.add(node.id);
            if (node.nodeType === ESSVNode.REQUIREMENT && node.requirements) {
                for (const requirement of node.requirements) {
                    if (requirement.nextNode) {
                        if (stack.has(requirement.nextNode)) {
                            return true; // Circular reference detected
                        }
                        if (!visitedNodes.has(requirement.nextNode)) {
                            const nextNode = this.config.getNode(requirement.nextNode);
                            if (!nextNode) {
                                // Something went wrong, we have already checked that the next nodes of all options are valid
                                throw new Error(`Node with ID ${requirement.nextNode} not found while traversing from requirement with ID ${requirement.id}`);
                            }
                            if (dfs(nextNode, visitedNodes, stack)) {
                                return true;
                            }
                        }
                    }
                }
            }
            else {
                for (const option of node.options) {
                    if (option.nextNode) {
                        if (stack.has(option.nextNode)) {
                            return true; // Circular reference detected
                        }
                        if (!visitedNodes.has(option.nextNode)) {
                            const nextNode = this.config.getNode(option.nextNode);
                            if (!nextNode) {
                                // Something went wrong, we have already checked that the next nodes of all options are valid
                                throw new Error(`Node with ID ${option.nextNode} not found while traversing from option with ID ${option.id}`);
                            }
                            if (dfs(nextNode, visitedNodes, stack)) {
                                return true;
                            }
                        }
                    }
                }
            }
            stack.delete(node.id);
            return false;
        };
        return dfs(startNode, new Set(), new Set());
    }
    validateConfig() {
        // Perform comprehensive validation of the entire configuration
        // Validate each node
        for (const node of Array.from(this.config.nodeMap.values())) {
            if (!this.validateNode(node)) {
                console.warn('Validation failed - validateNode', node);
                return false;
            }
        }
        // Validate each option
        for (const option of Array.from(this.config.optionMap.values())) {
            if (!this.validateOption(option)) {
                console.warn('Validation failed - validateOption', option);
                return false;
            }
        }
        // Validate each requirement
        for (const requirement of Array.from(this.config.requirementMap.values())) {
            if (!this.validateRequirement(requirement)) {
                console.warn('Validation failed - validateRequirement', requirement);
                return false;
            }
        }
        // After we know the nodes, options and requirements all have valid attributes, we validate the SSV
        // Ensure there is exactly one Requirement node
        if (!this.validateSingleRequirementNode()) {
            console.warn('Validation failed - validateSingleRequirementNode');
            return false;
        }
        // Check for orphan nodes
        if (this.detectOrphanNodes()) {
            console.warn('Validation failed - detectOrphanNodes');
            return false;
        }
        // Check for circular references
        if (this.detectCircularReferences()) {
            console.warn('Validation failed - detectCircularReferences');
            return false;
        }
        // Additional comprehensive validations if any can be added here
        return true;
    }
}
export class SSVConfig {
    constructor(data) {
        const configData = JSON.parse(JSON.stringify(data));
        this.nodeMap = new Map(configData.nodes.map((node) => [node.id, node]));
        const allOptions = configData.nodes.flatMap((node) => node.options || []);
        this.optionMap = new Map(allOptions.map((option) => [option.id, option]));
        this.requirementMap = new Map(configData.requirements.map((req) => [req.id, req]));
        this.injectRequirements();
        this.validator = new SSVValidator(this);
    }
    isValid() {
        return this.validator.validateConfig();
    }
    getNode(id) {
        return this.nodeMap.get(id);
    }
    getOption(id) {
        return this.optionMap.get(id);
    }
    getRequirement(id) {
        return this.requirementMap.get(id);
    }
    getRequirementNodes() {
        return [...this.nodeMap.values()].filter(node => node.nodeType === 'Requirement');
    }
    // We inject the requirements list into the start node
    injectRequirements() {
        for (const node of this.getRequirementNodes()) {
            node.requirements = [...this.requirementMap.values()];
        }
    }
    getStartNode() {
        return this.getRequirementNodes()[0] || null;
    }
}
export class SSVRunner {
    constructor(ssvConfig) {
        this.pathHistory = [];
        this.responses = new Map();
        this.state = ESSVRunnerState.WAITING;
        this.sessionId = uuidv4();
        this.sessionStartTime = null;
        this.sessionEndTime = null;
        this.outcome = null;
        this.outcomeText = null;
        this.currentNode = null;
        this.config = ssvConfig;
        this.state = this.config.isValid() ? ESSVRunnerState.READY : ESSVRunnerState.INVALID;
    }
    getState() {
        return this.state;
    }
    start() {
        if (this.state !== ESSVRunnerState.READY) {
            console.warn('SSV Runner not ready');
            return;
        }
        this.sessionStartTime = new Date();
        this.currentNode = this.config.getStartNode();
        this.state = ESSVRunnerState.RUNNING;
    }
    resume(nodeId) {
        // TODO: this is a very bad idea
        this.outcome = null;
        this.outcomeText = null;
        this.sessionEndTime = null;
        this.state = ESSVRunnerState.RUNNING;
        this.jumpTo(nodeId);
    }
    next(nodeId) {
        if (this.state !== ESSVRunnerState.RUNNING) {
            console.warn('SSV Runner not running');
            return;
        }
        // If the outcome is rejected, we stop
        if (this.getOutcome() === ESSVOutcome.REJECTED) {
            this.completed();
        }
        // if the outcome is pipeline with no explicit next node, we stop here
        if (this.getOutcome() === ESSVOutcome.PIPELINE && !nodeId) {
            this.completed();
        }
        // if we have a positive outcome or no outcome, we can continue with explicit or default nextNode
        const nextId = nodeId || (this.currentNode ? this.currentNode.nextNode : undefined);
        if (nextId) {
            // if there is a nextId, we check if we have a node for that
            const nextNode = this.config.getNode(nextId);
            if (!nextNode) {
                // We should have this node loaded, if it's not then there was an error
                this.newError(`Error navigating to node with id ${nextId}, it does not exist`);
                return;
            }
            this.currentNode = nextNode;
        }
        else {
            // if we have a positive or no outcome, at a terminal node, we're done
            this.completed();
        }
    }
    canBack() {
        if (this.state !== ESSVRunnerState.RUNNING) {
            return false;
        }
        return this.pathHistory.length > 0;
    }
    back() {
        if (this.canBack()) {
            this.jumpTo(this.pathHistory[this.pathHistory.length - 1]);
        }
    }
    jumpTo(nodeId) {
        const nodeIndex = this.pathHistory.indexOf(nodeId);
        if (nodeIndex > -1) {
            const nextNode = this.config.getNode(nodeId);
            if (!nextNode) {
                this.newError(`Error navigating to node with id ${nodeId}, it does not exist`);
                return;
            }
            // Update the path history to remove any nodes visited after the target node
            this.pathHistory = this.pathHistory.slice(0, nodeIndex);
            // Delete responses captured to this point
            const pathHistorySet = new Set(this.pathHistory);
            for (const key of this.responses.keys()) {
                if (!pathHistorySet.has(key)) {
                    this.responses.delete(key);
                }
            }
            // Update the current node ID to the specified node ID
            this.currentNode = nextNode;
        }
        else {
            this.newError(`Error jumping to node ${nodeId} - node not found in path history.`);
        }
    }
    capture(nodeId, value) {
        const node = this.config.getNode(nodeId);
        if (!node) {
            return;
        }
        // Updating the pathHistory and responses before navigating to the next node
        this.pathHistory.push(nodeId);
        this.responses.set(nodeId, { node, value, sessionId: this.sessionId });
        let outcome;
        let outcomeText;
        let nextNodeId;
        switch (node.nodeType) {
            case ESSVNode.REQUIREMENT: {
                if (typeof value !== 'number') {
                    this.newError('Incorrect capture type');
                    return;
                }
                const requirement = this.config.getRequirement(value);
                if (!requirement) {
                    this.newError('Requirement not found');
                    return;
                }
                outcome = requirement.outcome;
                outcomeText = requirement.outcomeText;
                nextNodeId = requirement.nextNode;
                break;
            }
            case ESSVNode.ASSESSMENT: {
                if (typeof value !== 'number') {
                    this.newError('Incorrect capture type');
                    return;
                }
                const option = this.config.getOption(value);
                if (!option) {
                    this.newError('Option not found');
                    return;
                }
                outcome = option.outcome;
                outcomeText = option.outcomeText;
                nextNodeId = option.nextNode;
                break;
            }
            default: {
                this.newError('Unknown node type in capture');
                return;
            }
        }
        if (this.setOutcome(outcome) && outcomeText) {
            this.outcomeText = outcomeText;
        }
        this.next(nextNodeId);
    }
    onComplete(callback) {
        this.completedCallback = callback;
    }
    completed() {
        let finalOutcome = this.getOutcome();
        // TODO: is this the best approach?
        // if the final outcome is inconclusive it might be positive
        if (!finalOutcome) {
            // If there's responses captured we assume it's positive, else we reject
            finalOutcome = this.pathHistory.length > 0 ? ESSVOutcome.APPROVED : ESSVOutcome.REJECTED;
            this.setOutcome(finalOutcome);
        }
        // A positive outcome should be validated
        if ([ESSVOutcome.APPROVED, ESSVOutcome.PIPELINE].includes(finalOutcome)) {
            // check if project data is sufficient before allowing project creation
            if (!this.hasProjectData()) {
                finalOutcome = ESSVOutcome.REJECTED;
                this.setOutcome(finalOutcome);
            }
        }
        this.setOutcome(finalOutcome);
        this.state = ESSVRunnerState.COMPLETE;
        this.sessionEndTime = new Date();
        if (this.completedCallback) {
            this.completedCallback(finalOutcome);
        }
    }
    // Return true when a new outcome has been set
    setOutcome(outcome) {
        const currentOutcome = this.getOutcome();
        // Outcome shouldn't be set to undefined
        if (typeof outcome === 'undefined') {
            return false;
        }
        // A set outcome can not be changed to null or undefined
        if (!outcome && currentOutcome) {
            return false;
        }
        // A set outcome can not be changed to null or undefined
        if (!outcome && currentOutcome) {
            return false;
        }
        const outcomeRank = {
            [ESSVOutcome.APPROVED]: 1,
            [ESSVOutcome.PIPELINE]: 2,
            [ESSVOutcome.REJECTED]: 3,
        };
        const getOutcomeRank = (rankOutcome) => {
            return outcomeRank[rankOutcome] || 0;
        };
        if (!currentOutcome || getOutcomeRank(outcome) > getOutcomeRank(currentOutcome)) {
            this.outcome = outcome;
            return true;
        }
        return false;
    }
    getOutcome() {
        // TODO: We are not supporting pipeline projects
        if (this.outcome === ESSVOutcome.PIPELINE) {
            this.outcome = ESSVOutcome.REJECTED;
        }
        return this.outcome;
    }
    getOutcomeText() {
        if (this.outcomeText) {
            return this.outcomeText;
        }
        switch (this.getOutcome()) {
            case (ESSVOutcome.REJECTED): {
                return 'Unfortunately, you will not be able to use ’self-serve’ for this.'
                    + ' Please get in touch with the procurement team for further information';
            }
            case (ESSVOutcome.PIPELINE): {
                return 'This looks like a requirement that needs to be overseen by Procurement.'
                    + ' Should we raise a ticket with them on your behalf?';
            }
            case (ESSVOutcome.APPROVED): {
                return 'From the answers you have provided, we can confirm you will be able to ‘self-serve’ this contract.'
                    + ' To do this, please go through the following steps…';
            }
            default: {
                return '';
            }
        }
    }
    getResponse(nodeId) {
        return this.responses.get(nodeId);
    }
    getSession() {
        return {
            sessionId: this.sessionId,
            startTime: this.sessionStartTime,
            endTime: this.sessionEndTime,
            outcome: this.getOutcome()
        };
    }
    getResponses() {
        return Array.from(this.responses.values());
    }
    getSummary() {
        const getOptionLabel = (node, value) => {
            switch (node.nodeType) {
                case (ESSVNode.REQUIREMENT): {
                    if (typeof value !== 'number') {
                        return '';
                    }
                    const requirement = this.config.requirementMap.get(value);
                    return requirement ? requirement.label : '';
                }
                case (ESSVNode.ASSESSMENT): {
                    if (typeof value !== 'number') {
                        return '';
                    }
                    const option = this.config.optionMap.get(value);
                    return option ? option.label : '';
                }
                default: {
                    return '';
                }
            }
        };
        const results = [];
        for (const response of this.getResponses()) {
            const valueDescription = getOptionLabel(response.node, response.value);
            results.push(Object.assign(Object.assign({}, response), { description: `${response.node.label.replace(/(.*)(\?)(.*)/, '$1$3')}:  ${valueDescription}`, valueDescription }));
        }
        return results;
    }
    getProjectData() {
        const data = {};
        const requirementResponse = [...this.responses.values()]
            .find((el) => el.node.nodeType === ESSVNode.REQUIREMENT);
        if (requirementResponse) {
            if (typeof requirementResponse.value === 'number') {
                const selectedRequirement = this.config.getRequirement(requirementResponse.value);
                if (selectedRequirement && selectedRequirement.label) {
                    data.name = selectedRequirement.label;
                }
                if (selectedRequirement && selectedRequirement.categoryId) {
                    data.categoryId = selectedRequirement.categoryId;
                }
            }
        }
        if (this.getOutcome() === ESSVOutcome.APPROVED) {
            data.type = 'Self-Service';
        }
        else if (this.getOutcome() === ESSVOutcome.PIPELINE) {
            data.type = 'Pipeline';
        }
        return data;
    }
    hasProjectData() {
        const data = this.getProjectData();
        return data.name && data.categoryId && data.type;
    }
    newError(message) {
        this.state = ESSVRunnerState.ERROR;
        console.warn(message);
    }
}
